import type { dia } from '@joint/core'
import type { TemplateRelationType } from '../../../map-editor-entities/schema/templateSchema'
import { routeLayerToCategory } from '../../../map-editor-shared/lib/layerUtils'
import { isCctvElement } from './zoneCctvRelations'

export const ZONE_CCTV_ID_RELATION = '__zone_cctv_by_id__'

export function isZoneCctvRelation(source: string, target: string): boolean {
  const a = routeLayerToCategory(source)
  const b = routeLayerToCategory(target)
  return (
    (a === 'zone' && (b === 'cctv' || b === 'warningCctv')) ||
    (b === 'zone' && (a === 'cctv' || a === 'warningCctv'))
  )
}

/** CCTV display ID: prefix_floor_ZONE_sequence, e.g. P1_B1_1_3. */
export function cctvZoneNumber(value: unknown): number | null {
  const parts = String(value ?? '')
    .trim()
    .split('_')
  if (
    parts.length !== 4 ||
    !parts[0] ||
    !parts[1] ||
    !/^\d+$/.test(parts[2]) ||
    !/^\d+$/.test(parts[3])
  )
    return null
  const number = Number(parts[2])
  return Number.isSafeInteger(number) && number > 0 ? number : null
}

export function namedZoneNumber(value: unknown): number | null {
  const match = String(value ?? '')
    .trim()
    .match(/^(?:구역\s*|zone\s*)?(\d+)\s*(?:번?\s*구역)?$/i)
  const number = match ? Number(match[1]) : NaN
  return Number.isSafeInteger(number) && number > 0 ? number : null
}

function isZone(element: dia.Element): boolean {
  const data = element.get('data') || {}
  return (
    data.properties?.kind === 'zone' ||
    routeLayerToCategory(data.layer || data.type || '') === 'zone'
  )
}

export function planZoneCctvIdRelations(graph: dia.Graph) {
  const elements = graph.getElements()
  const zones = elements.filter(isZone)
  const byNumber = new Map<number, dia.Element[]>()
  for (const zone of zones) {
    const number = namedZoneNumber(zone.get('data')?.properties?.name)
    if (number !== null) byNumber.set(number, [...(byNumber.get(number) || []), zone])
  }
  const assignments = elements.filter(isCctvElement).map((camera) => {
    const props = camera.get('data')?.properties || {}
    const name = String(props.name || props.id || '')
    const number = cctvZoneNumber(name)
    const candidates = number === null ? [] : byNumber.get(number) || []
    const zone = candidates.length === 1 ? candidates[0] : null
    const reason =
      number === null
        ? 'ID 형식 오류'
        : candidates.length > 1
          ? '구역 번호 중복'
          : !zone
            ? '해당 구역 없음'
            : ''
    return { camera, zone, name, reason }
  })
  return { zones, assignments }
}

/** Only auto-relationship commands call this; zone creation retains its spatial policy. */
export function linkZonesByCctvId(
  graph: dia.Graph,
  relations: Record<string, TemplateRelationType>,
  selectedId?: string,
) {
  const plan = planZoneCctvIdRelations(graph)
  const zoneKeys = new Set(['cctvIds', 'memberIds'])
  const cameraKeys = new Set(['zoneId'])
  for (const config of Object.values(relations)) {
    if (!isZoneCctvRelation(config.sourceType, config.targetType)) continue
    if (routeLayerToCategory(config.sourceType) === 'zone') zoneKeys.add(config.propertyKey)
    else cameraKeys.add(config.propertyKey)
  }
  const selected = selectedId ? graph.getCell(selectedId) : null
  const assignments = !selectedId
    ? plan.assignments
    : plan.assignments.filter((item) => {
        if (!selected?.isElement()) return false
        if (isCctvElement(selected as dia.Element)) return String(item.camera.id) === selectedId
        if (!isZone(selected as dia.Element)) return false
        const props = item.camera.get('data')?.properties || {}
        const zoneProps = selected.get('data')?.properties || {}
        return (
          String(item.zone?.id) === selectedId ||
          [...cameraKeys].some((key) => String(props[key]) === selectedId) ||
          [...zoneKeys].some(
            (key) =>
              Array.isArray(zoneProps[key]) &&
              zoneProps[key].map(String).includes(String(item.camera.id)),
          )
        )
      })
  if (selectedId && assignments.length === 0) return { linked: 0, unmatched: [] as string[] }
  const affected = new Set(assignments.map((item) => String(item.camera.id)))
  graph.startBatch('zone-cctv-by-id')
  try {
    for (const zone of plan.zones) {
      const data = zone.get('data') || {}
      const props = { ...data.properties }
      const targets = assignments
        .filter((item) => item.zone === zone)
        .map((item) => String(item.camera.id))
      for (const key of zoneKeys) {
        const retained =
          selectedId && Array.isArray(props[key])
            ? props[key].filter((id: unknown) => !affected.has(String(id)))
            : []
        props[key] = [...retained, ...targets]
      }
      zone.set('data', { ...data, properties: props })
    }
    for (const { camera, zone } of assignments) {
      const data = camera.get('data') || {}
      const props = { ...data.properties }
      for (const key of cameraKeys) props[key] = zone ? String(zone.id) : ''
      camera.set('data', { ...data, properties: props })
    }
  } finally {
    graph.stopBatch('zone-cctv-by-id')
  }
  return {
    linked: assignments.filter((item) => item.zone).length,
    unmatched: assignments
      .filter((item) => !item.zone)
      .map((item) => `${item.name || item.camera.id}: ${item.reason}`),
  }
}
